import { apply, give, onceToday, toast } from '../core/effects';
import { S } from '../core/game';
import { clock, m } from '../i18n';

import { objName } from '../i18n/labels';
import { sfx } from '../audio/sfx';
import { sayEnglish } from '../audio/speech';
import { say, type Choice } from '../ui/dialog';
import { playFlight } from '../ui/flightScene';
import { MAPS } from '../world/map';
import { payOrCard, payPlan, account } from './bank';
import { stamp } from './passport';
import { registerObjects } from './registry';
import { travelTo } from './trips';
import { isVehicle, riding } from './places';

/**
 * Taoyuan Airport: the Airport MRT from Taipei, check-in in English, the security quiz,
 * the gate, and the long flight to Barcelona (and back).
 */
export const FLIGHT_PRICE = 300;
const LAST_FLIGHT = 18 * 60;
const FLIGHT_MINUTES = 240;
const MRT_MINUTES = 40;

const T = () => m().airport.texts;
const shuffle = <T>(a: T[]): T[] => a.map((v) => [Math.random(), v] as const).sort((x, y) => x[0] - y[0]).map((p) => p[1]);

function toAirport(): void {
  const t = T();
  say({
    who: objName('ap_mrtTaipei'),
    icon: '✈️',
    text: t.mrtToAirport,
    choices: [
      {
        text: t.mrtGo,
        cost: m().common.minutes(MRT_MINUTES),
        run: () => {
          if (isVehicle(riding.on)) riding.on = null;
          sfx('ring');
          apply({ minutes: MRT_MINUTES }, { float: false });
          stamp('mrt');
          travelTo('airport', MAPS.airport.spots!.mrt);
        },
      },
      { text: m().common.back },
    ],
  });
}

function backToTaipei(): void {
  const t = T();
  say({
    who: objName('ap_mrt'),
    icon: '🚇',
    text: t.mrtBackText,
    choices: [
      { text: t.mrtBack, cost: m().common.minutes(MRT_MINUTES), run: () => (sfx('ring'), apply({ minutes: MRT_MINUTES }, { float: false }), travelTo('taipei', MAPS.taipei.spots!.train)) },
      { text: m().common.back },
    ],
  });
}

function checkin(): void {
  const t = T();
  const who = objName('ap_checkin');
  const s = S();
  if (s.daily.flightTaken) return say({ who, icon: '🎫', text: t.flown });
  if (s.daily.flightBarcelona) return say({ who, icon: '🎫', text: t.haveTicket });
  if (s.time >= LAST_FLIGHT) return say({ who, icon: '🎫', text: t.tooLate(clock(LAST_FLIGHT)) });
  if (payPlan(FLIGHT_PRICE) === 'none') return say({ who, icon: '🎫', text: `${t.poor(FLIGHT_PRICE)}\n${m().airport.bank.cannotPay(FLIGHT_PRICE, s.coins, account(s).bank)}` });
  sayEnglish(t.checkin, 0.9);
  say({
    who,
    icon: '🎫',
    text: t.checkin,
    choices: [{ text: t.ticket(FLIGHT_PRICE), run: askTicket }, { text: m().common.back }],
  });
}

function askTicket(): void {
  const t = T();
  const who = objName('ap_checkin');
  const [ok, ...bad] = t.ask;
  say({
    who,
    icon: '🎫',
    text: t.checkinAsk,
    choices: shuffle([ok, ...bad]).map((o): Choice => ({
      text: `🗣️ ${o}`,
      run: () => {
        sayEnglish(o, 0.9);
        if (o !== ok) {
          sfx('bad');
          return say({ who, icon: '🎫', text: t.wrong, choices: [{ text: m().common.back, run: askTicket }] });
        }
        payOrCard(FLIGHT_PRICE, who, '🎫', () => {
          S().daily.flightBarcelona = true;
          sfx('ok');
          say({ who, icon: '🎫', text: t.bought });
        }, askTicket);
      },
    })),
  });
}

function security(): void {
  const t = T();
  const who = objName('ap_security');
  const s = S();
  if (!s.daily.flightBarcelona) return say({ who, icon: '🛂', text: t.securityNoTicket });
  if (s.daily.security) return say({ who, icon: '🛂', text: t.securityDone });
  const [ok, ...rest] = t.securityQ;
  say({
    who,
    icon: '🛂',
    text: t.securityText,
    choices: shuffle([ok, ...rest]).map((o): Choice => ({
      text: o,
      run: () => {
        if (o !== ok) {
          sfx('bad');
          return say({ who, icon: '🛂', text: t.securityWrong, choices: [{ text: m().common.back, run: security }] });
        }
        s.daily.security = true;
        sfx('ok');
        if (onceToday('ap.security')) apply({ stars: 2, study: 2 });
        say({ who, icon: '🛂', text: `${t.securityOk}\n${t.securityDone}` });
      },
    })),
  });
}

function gate(): void {
  const t = T();
  const who = objName('ap_gate');
  const s = S();
  if (!s.daily.flightBarcelona) return say({ who, icon: '✈️', text: t.needTicket });
  if (!s.daily.security) return say({ who, icon: '✈️', text: t.needSecurity });
  say({
    who,
    icon: '✈️',
    text: t.gateText,
    choices: [
      {
        text: t.board,
        cost: m().common.minutes(FLIGHT_MINUTES),
        run: () => {
          s.daily.flightTaken = true;
          stamp('airport');
          playFlight(t.flyTo, t.announce('Barcelona'), m().trips.ride.skip, () => {
            apply({ minutes: FLIGHT_MINUTES, energy: -10 }, { float: false });
            travelTo('barcelona', MAPS.barcelona.spots!.airport);
            window.setTimeout(() => toast(t.arrive(m().barcelona.mapNames.barcelona)), 900);
          });
        },
      },
      { text: m().common.back },
    ],
  });
}

/** The return flight from Barcelona's airport corner (object `bc_flight`). */
export function flightHome(): void {
  const t = T();
  const who = objName('bc_flight');
  say({
    who,
    icon: '✈️',
    text: t.gateText,
    choices: [
      {
        text: t.board,
        cost: m().common.minutes(FLIGHT_MINUTES),
        run: () => {
          const s = S();
          s.daily.flightBarcelona = false;
          s.daily.security = false;
          if (isVehicle(riding.on)) riding.on = null;
          playFlight(t.flyHome, t.announce('Taiwan'), m().trips.ride.skip, () => {
            apply({ minutes: FLIGHT_MINUTES, energy: -10 }, { float: false });
            travelTo('airport', MAPS.airport.spots!.gate);
            window.setTimeout(() => toast(t.homeGate), 900);
          }, true);
        },
      },
      { text: m().common.back },
    ],
  });
}

function info(): void {
  const t = T();
  const who = objName('ap_info');
  const [ok, ...rest] = t.infoA;
  say({
    who,
    icon: 'ℹ️',
    text: t.info,
    choices: [
      {
        text: `❓ ${t.infoQ}`,
        run: () =>
          say({
            who,
            icon: '❓',
            text: t.infoQ,
            choices: shuffle([ok, ...rest]).map((o): Choice => ({
              text: o,
              run: () => {
                if (o === ok) {
                  sfx('ok');
                  if (onceToday('ap.info')) apply({ stars: 2, study: 2 });
                  say({ who, icon: 'ℹ️', text: t.infoOk });
                } else {
                  sfx('bad');
                  say({ who, icon: 'ℹ️', text: t.infoWrong });
                }
              },
            })),
          }),
      },
      { text: m().common.back },
    ],
  });
}

function duty(): void {
  const t = T();
  const who = objName('ap_duty');
  say({
    who,
    icon: '🛍️',
    text: t.duty,
    choices: [
      {
        text: t.dutyBuy,
        run: () => {
          if (S().coins < 40) return toast(m().shop.poor);
          apply({ coins: -40 }, { float: false });
          give('pineappleCake');
          sfx('checkout');
        },
      },
      { text: m().common.back },
    ],
  });
}

registerObjects({
  ap_mrtTaipei: toAirport,
  ap_mrt: backToTaipei,
  ap_checkin: checkin,
  ap_security: security,
  ap_gate: gate,
  ap_info: info,
  ap_duty: duty,
  ap_belt: () => say({ who: objName('ap_belt'), icon: '🧳', text: T().belt }),
  bc_flight: flightHome,
});
